import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { FaSearch, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import logger from '../utils/logger';
import GoogleAuth from '../components/GoogleAuth';


function Submit() {
  const { user, loading, getValidJWTToken } = useAuth(); // Changed isLoading to loading, removed isAuthenticated
  const location = useLocation();
  const { t } = useLanguage();
  
  // Add a minimum loading time to prevent "shake" from React StrictMode double-render
  const [minLoadingComplete, setMinLoadingComplete] = useState(false);
  
  useEffect(() => {
    // Set a minimum 300ms loading time to prevent flickering
    const timer = setTimeout(() => {
      setMinLoadingComplete(true);
    }, 300);
    
    return () => clearTimeout(timer);
  }, []);
  
  // Extract streamer from URL - first check query params, then path
  const extractStreamerFromUrl = () => {
    // First, check query parameter (e.g., /submit?streamer=new-test)
    const urlParams = new URLSearchParams(location.search);
    const streamerFromQuery = urlParams.get('streamer');
    if (streamerFromQuery) {
      return streamerFromQuery;
    }
    
    // Fallback: check URL path (e.g., /seeagle -> seeagle)
    const pathParts = location.pathname.split('/').filter(Boolean);
    const firstPart = pathParts[0] || '';
    
    // List of known app routes that are NOT streamer names
    const knownRoutes = ['submit', 'vote', 'admin', 'streamer', 'login', 'application', 'terms', 'legal', 'gallery', 'shop'];
    
    // Only return the first part as streamer if it's not a known app route
    if (firstPart && !knownRoutes.includes(firstPart.toLowerCase())) {
      return firstPart;
    }
    
    return '';
  };
  
  const streamerFromUrl = extractStreamerFromUrl();
  
  // We no longer redirect to login when viewing the page
  // Authentication will be checked only when submitting
  
  const initialChallengeId = new URLSearchParams(location.search).get('challenge') || 'normal';
  
  // Debug logging
  logger.log('📍 Submit.js Debug:', {
    pathname: location.pathname,
    search: location.search,
    streamerFromUrl,
    initialChallengeId
  });
  logger.log('Initial challenge ID from URL:', initialChallengeId);
  
  // Default form state with potential challenge from URL
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    title: '',
    challengeId: initialChallengeId, // Use challenge from URL if available
    website: '', // Selected streamer website (e.g., 'boblegob.com')
    products: {
      all: false,
      mug: false,
      poster: false,
      casquette: false,
      totebag: false,
      tshirt: false,
    },
    termsAccepted: false
  });
  
  // Pre-fill username and email from logged-in user
  useEffect(() => {
    if (user) { // Removed isAuthenticated check
      setFormData(prevData => ({
        ...prevData,
        username: user.username || user.name || '',
        email: user.email || ''
      }));
    }
  }, [user]); // Removed isAuthenticated from dependencies
  
  // Selected challenge state - initialized with URL challenge or default
  const [selectedChallenge, setSelectedChallenge] = useState(
    initialChallengeId !== 'normal' 
      ? { id: initialChallengeId, title: 'Loading...' } // Will be updated when challenges load
      : { id: 'normal', title: 'Normal' }
  );
  
  // Challenge dropdown state
  const [challenges, setChallenges] = useState([]);
  const [isLoadingChallenges, setIsLoadingChallenges] = useState(false);
  const [showChallengeDropdown, setShowChallengeDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const challengeDropdownRef = useRef(null);
  
  // Streamer dropdown state
  const [availableStreamers, setAvailableStreamers] = useState([]);
  const [selectedStreamer, setSelectedStreamer] = useState({ name: '', website: '' });
  const [isLoadingStreamers, setIsLoadingStreamers] = useState(false);
  const [showStreamerDropdown, setShowStreamerDropdown] = useState(false);
  const streamerDropdownRef = useRef(null);
  
  const [previewFile, setPreviewFile] = useState(null);
  const [previewFileUrl, setPreviewFileUrl] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    if (name === 'termsAccepted') {
      setFormData({
        ...formData,
        termsAccepted: checked
      });
    } else if (name === 'all') {
      // When "all" is checked/unchecked, update all product checkboxes
      setFormData({
        ...formData,
        products: {
          all: checked,
          mug: checked,
          poster: checked,
          casquette: checked,
          totebag: checked,
          tshirt: checked,
        }
      });
    } else {
      // When individual product is changed, update it and check if all are selected
      const updatedProducts = {
        ...formData.products,
        [name]: checked
      };
      // Check if all individual products are selected
      const allSelected = updatedProducts.mug && updatedProducts.poster && 
                         updatedProducts.casquette && updatedProducts.totebag && 
                         updatedProducts.tshirt;
      updatedProducts.all = allSelected;
      
      setFormData({
        ...formData,
        products: updatedProducts
      });
    }
  };
  
  const [checkingBackground, setCheckingBackground] = useState(false);

  const validateFile = (file) => {
    const validTypes = ['image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setSubmitError('onlyPngWebpAllowed');
      return false;
    }

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setSubmitError('fileTooLargeUpload');
      return false;
    }
    
    return true;
  };

  const checkTransparency = (file) => {
    return new Promise((resolve) => {
      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;

        // Sample edge pixels: top row, bottom row, left column, right column + corners
        const w = canvas.width;
        const h = canvas.height;
        let transparentCount = 0;
        let sampledCount = 0;

        const samplePixel = (x, y) => {
          const idx = (y * w + x) * 4;
          sampledCount++;
          if (data[idx + 3] < 240) transparentCount++;
        };

        // Sample corners (4 corners, 5x5 block each)
        const cornerSize = Math.min(5, Math.floor(w / 4), Math.floor(h / 4));
        for (let dy = 0; dy < cornerSize; dy++) {
          for (let dx = 0; dx < cornerSize; dx++) {
            samplePixel(dx, dy);
            samplePixel(w - 1 - dx, dy);
            samplePixel(dx, h - 1 - dy);
            samplePixel(w - 1 - dx, h - 1 - dy);
          }
        }

        // Sample edges at intervals
        const step = Math.max(1, Math.floor(Math.max(w, h) / 50));
        for (let x = 0; x < w; x += step) {
          samplePixel(x, 0);
          samplePixel(x, h - 1);
        }
        for (let y = 0; y < h; y += step) {
          samplePixel(0, y);
          samplePixel(w - 1, y);
        }

        URL.revokeObjectURL(url);

        const transparentRatio = transparentCount / sampledCount;
        resolve(transparentRatio >= 0.05);
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(false);
      };

      img.src = url;
    });
  };
  
  const handlePreviewFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file || !validateFile(file)) return;

    setCheckingBackground(true);
    setSubmitError('');

    const hasTransparency = await checkTransparency(file);
    setCheckingBackground(false);

    if (!hasTransparency) {
      setSubmitError('transparentBackgroundRequired');
      e.target.value = '';
      return;
    }

    setPreviewFile(file);
    const fileUrl = URL.createObjectURL(file);
    setPreviewFileUrl(fileUrl);
  };
  
  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };
  
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };
  
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) {
      setIsDragging(true);
    }
  };
  
  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      
      if (validateFile(file)) {
        setCheckingBackground(true);
        setSubmitError('');

        const hasTransparency = await checkTransparency(file);
        setCheckingBackground(false);

        if (!hasTransparency) {
          setSubmitError('transparentBackgroundRequired');
          return;
        }

        setPreviewFile(file);
        const fileUrl = URL.createObjectURL(file);
        setPreviewFileUrl(fileUrl);
        logger.log('File dropped:', file.name);
      }
    }
  };
  
  // Production file handling removed as requested
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [uploadLimit, setUploadLimit] = useState({ used: 0, limit: 3, remaining: 3 });
  


  // Handle challenge selection
  const handleChallengeSelect = (challenge) => {
    setSelectedChallenge(challenge);
    setFormData({
      ...formData,
      challengeId: challenge.id
    });
    setShowChallengeDropdown(false);
    setSearchQuery('');
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    // Debounce search to avoid too many API calls
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    searchTimeout.current = setTimeout(() => {
      fetchChallengesAndSelect(e.target.value);
    }, 300);
  };

  // Handle streamer selection
  const handleStreamerSelect = (streamerName) => {
    const website = `${streamerName}.com`;
    setSelectedStreamer({ name: streamerName, website });
    setFormData({
      ...formData,
      website
    });
    setShowStreamerDropdown(false);
  };

  // Fetch available streamers
  const fetchStreamers = async () => {
    try {
      setIsLoadingStreamers(true);
      
      const response = await fetch('/.netlify/functions/get-websites');
      const result = await response.json();
      
      if (response.ok && result.success && result.websites) {
        setAvailableStreamers(result.websites);
      } else {
        logger.error('Failed to fetch streamers:', result.message);
        setAvailableStreamers([]);
      }
    } catch (error) {
      logger.error('Error fetching streamers:', error);
      setAvailableStreamers([]);
    } finally {
      setIsLoadingStreamers(false);
    }
  };

  // Reference for search timeout
  const searchTimeout = useRef(null);

  // Create a ref to store the challenge ID from URL
  const challengeIdFromUrlRef = useRef(null);
  
  // Extract challenge ID from URL on mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const challengeId = params.get('challenge');
    if (challengeId) {
      logger.log(`Found challenge ID in URL: ${challengeId}`);
      challengeIdFromUrlRef.current = challengeId;
      
      // Immediately update form data with the challenge ID
      setFormData(prevData => ({
        ...prevData,
        challengeId: challengeId
      }));
    }
  }, [location.search]);
  
  // Modified challenge fetching function to handle URL parameter and filter expired challenges
  const fetchChallengesAndSelect = useCallback(async (query = '') => {
    setIsLoadingChallenges(true);
    try {
      // Build URL with website parameter if streamer is from URL
      let url = '/.netlify/functions/get-challenges';
      const params = new URLSearchParams();
      
      if (query) {
        params.append('query', query);
      }
      
      if (streamerFromUrl) {
        params.append('website', streamerFromUrl);
        logger.log(`Fetching challenges for website: ${streamerFromUrl}`);
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      logger.log('🔍 Fetching challenges from URL:', url);
      const response = await fetch(url);
      if (!response.ok) {
        logger.error('❌ Challenge fetch failed:', response.status, response.statusText);
        throw new Error(`Failed to fetch challenges: ${response.status}`);
      }
      const data = await response.json();
      logger.log('📋 Raw challenge data received:', data);
      const allChallenges = data.challenges || [];
      logger.log(`📊 Total challenges received: ${allChallenges.length}`);
      
      // Filter out expired challenges
      const now = new Date();
      logger.log(`Current date for challenge filter: ${now.toString()}`);
      
      const activeChallenges = allChallenges.filter(challenge => {
        // Always keep the 'normal' option
        if (challenge.id === 'normal') return true;
        
        // If challenge has no expiration, keep it
        if (!challenge.expiration) return true;
        
        // Parse the expiration date and compare with current time
        try {
          // Parse using explicit UTC time to avoid timezone issues
          const expirationStr = challenge.expiration;
          const expYear = parseInt(expirationStr.substring(0, 4));
          const expMonth = parseInt(expirationStr.substring(5, 7)) - 1; // JS months are 0-indexed
          const expDay = parseInt(expirationStr.substring(8, 10));
          const expHour = parseInt(expirationStr.substring(11, 13) || '0');
          const expMinute = parseInt(expirationStr.substring(14, 16) || '0');
          const expSecond = parseInt(expirationStr.substring(17, 19) || '0');
          
          // Create date in UTC
          const expirationDate = new Date(Date.UTC(expYear, expMonth, expDay, expHour, expMinute, expSecond));
          
          logger.log(`Challenge ${challenge.title} (${challenge.id}): Expiration ${expirationDate.toUTCString()}, Now: ${now.toUTCString()}`);
          logger.log(`Is active: ${expirationDate > now}`);
          
          return expirationDate > now; // Only keep challenges that haven't expired
        } catch (e) {
          console.warn(`Invalid expiration date for challenge ${challenge.id}:`, e);
          return true; // Keep challenges with invalid dates (fail safe)
        }
      });
      
      logger.log(`Filtered ${allChallenges.length - activeChallenges.length} expired challenges`);
      logger.log('✅ Setting active challenges:', activeChallenges);
      setChallenges(activeChallenges);
      
      // After challenges are loaded, check if we need to select one from URL
      if (challengeIdFromUrlRef.current) {
        logger.log('Looking for challenge with ID:', challengeIdFromUrlRef.current);
        
        // Handle type mismatch between URL parameter (string) and challenge ID (could be number)
        const urlChallengeId = challengeIdFromUrlRef.current;
        const preSelectedChallenge = activeChallenges.find(challenge => 
          String(challenge.id) === String(urlChallengeId)
        );
        
        logger.log('Found matching challenge:', preSelectedChallenge);
        
        if (preSelectedChallenge) {
          logger.log('Setting selected challenge to:', preSelectedChallenge.title);
          // Set the selected challenge
          setSelectedChallenge(preSelectedChallenge);
          setFormData(prevFormData => ({
            ...prevFormData,
            challengeId: preSelectedChallenge.id
          }));
          // Clear the ref so we don't try to select it again
          challengeIdFromUrlRef.current = null;
        } else {
          console.warn(`Challenge with ID ${urlChallengeId} not found in active challenges or may have expired`);
        }
      }
    } catch (error) {
      logger.error('💥 Error fetching challenges:', error);
    } finally {
      logger.log('🏁 Setting isLoadingChallenges to false');
      setIsLoadingChallenges(false);
    }
  }, [streamerFromUrl]); // Add streamerFromUrl as dependency
  
  // Auto-select streamer from URL and set initial form data
  useEffect(() => {
    if (streamerFromUrl) {
      logger.log(`Auto-selecting streamer from URL: ${streamerFromUrl}`);
      const website = `${streamerFromUrl}.com`;
      setSelectedStreamer({ name: streamerFromUrl, website });
      setFormData(prevData => ({
        ...prevData,
        website
      }));
    }
  }, [streamerFromUrl]);

  // Fetch challenges on component mount
  useEffect(() => {
    fetchChallengesAndSelect();
    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, [fetchChallengesAndSelect]);

  // Fetch streamers on component mount
  useEffect(() => {
    fetchStreamers();
  }, []);

  // Scroll to top when the component mounts (fixes navigation scroll issue)
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (challengeDropdownRef.current && !challengeDropdownRef.current.contains(event.target)) {
        setShowChallengeDropdown(false);
      }
      if (streamerDropdownRef.current && !streamerDropdownRef.current.contains(event.target)) {
        setShowStreamerDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Fetch upload limit data on component mount
  useEffect(() => {
    const fetchUploadLimit = async () => {
      try {
        const response = await fetch('/.netlify/functions/upload?action=limit');
        if (response.ok) {
          const data = await response.json();
          setUploadLimit(data);
        }
      } catch (error) {
        logger.error('Error fetching upload limit:', error);
      }
    };
    
    fetchUploadLimit();
  }, [submitSuccess]); // Refresh when submission is successful

  // Server-side upload function with direct Cloudinary upload
  const uploadToServer = async (files, formFields) => {
    try {
      // Get valid authentication token with auto-logout if expired
      let authHeader = '';
      try {
        const authToken = await getValidJWTToken();
        if (authToken) {
          authHeader = `Bearer ${authToken}`;
          logger.log('✅ Using valid JWT token for upload');
        } else {
          logger.log('ℹ️ No JWT token available (auto-logged out if expired), proceeding as anonymous');
        }
      } catch (error) {
        console.warn('⚠️ Error getting JWT token, proceeding as anonymous:', error);
      }

      // Step 1: Upload image directly to Cloudinary
      logger.log('☁️ Uploading image directly to Cloudinary...');
      let imageUrl = null;
      let publicId = null;
      
      if (files.previewFile) {
        try {
          // Get Cloudinary signature
          logger.log('🔐 Getting Cloudinary signature...');
          const signatureResponse = await fetch('/.netlify/functions/get-cloudinary-signature', {
            method: 'POST',
            headers: {
              'Authorization': authHeader,
              'Content-Type': 'application/json'
            }
          });
          
          if (!signatureResponse.ok) {
            throw new Error('Failed to get Cloudinary signature');
          }
          
          const signatureData = await signatureResponse.json();
          logger.log('✅ Got Cloudinary signature');
          
          // Prepare image upload to Cloudinary
          const cloudinaryFormData = new FormData();
          cloudinaryFormData.append('file', files.previewFile);
          cloudinaryFormData.append('api_key', signatureData.apiKey);
          cloudinaryFormData.append('timestamp', signatureData.timestamp);
          cloudinaryFormData.append('signature', signatureData.signature);
          cloudinaryFormData.append('folder', signatureData.folder);
          
          logger.log('📤 Uploading image to Cloudinary (direct)...');
          logger.log('  - File:', files.previewFile.name, (files.previewFile.size / (1024 * 1024)).toFixed(1) + 'MB');
          
          // Upload directly to Cloudinary
          const cloudinaryResponse = await fetch(`https://api.cloudinary.com/v1_1/${signatureData.cloudName}/image/upload`, {
            method: 'POST',
            body: cloudinaryFormData
          });
          
          if (!cloudinaryResponse.ok) {
            const errorText = await cloudinaryResponse.text();
            throw new Error(`Cloudinary upload failed: ${errorText}`);
          }
          
          const cloudinaryResult = await cloudinaryResponse.json();
          imageUrl = cloudinaryResult.secure_url;
          publicId = cloudinaryResult.public_id;
          
          logger.log('✅ Image uploaded to Cloudinary:', publicId);
          logger.log('📸 Image URL:', imageUrl);
          
        } catch (cloudinaryError) {
          logger.error('❌ Cloudinary upload failed:', cloudinaryError);
          throw new Error(`Image upload failed: ${cloudinaryError.message}`);
        }
      }
      
      // Step 2: Submit upload data (without image file)
      logger.log('📤 Submitting upload data to backend...');
      
      // Handle challenge ID with special handling for "normal"
      const challengeId = formFields.challengeId === 'normal' ? '0' : formFields.challengeId;
      
      const uploadData = {
        username: formFields.username,
        email: formFields.email,
        title: formFields.title,
        products: formFields.products,
        termsAccepted: formFields.termsAccepted,
        website: formFields.website,
        challengeId: challengeId,
        imageUrl: imageUrl,  // Cloudinary URL
        publicId: publicId   // Cloudinary public ID
      };
      
      logger.log('📋 Upload data:', uploadData);
      
      const response = await fetch('/.netlify/functions/upload', {
        method: 'POST',
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(uploadData)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Upload failed with status: ${response.status}`);
      }
      
      const data = await response.json();
      logger.log('✅ Upload response:', data);
      return data;
    } catch (error) {
      logger.error('Error uploading data to server:', error);
      throw error;
    }
  };

  // We no longer show a login modal
  // Security is enforced only on the server-side through the auth token

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    // We no longer check authentication on the client side
    // The server will handle authentication through the token
    // and return appropriate error responses if not authenticated
    
    // Clear previous errors first
    setSubmitError('');
    setIsSubmitting(true);
    
    try {
      // Client-side validations
      // Validate files
      if (!previewFile) {
        throw new Error(t('previewFileRequired'));
      }
      
      // Validate that at least one product is selected
      const hasSelectedProduct = Object.values(formData.products).some(value => value === true);
      if (!hasSelectedProduct) {
        throw new Error(t('selectAtLeastOneProduct'));
      }
      
      // Validate other required fields
      if (!formData.username || !formData.email || !formData.title) {
        throw new Error(t('fillAllRequiredFields'));
      }
      
      // Validate streamer selection
      if (!formData.website || !selectedStreamer.name) {
        throw new Error(t('pleaseSelectStreamer'));
      }
      
      // Validate terms acceptance
      if (!formData.termsAccepted) {
        throw new Error(t('mustAcceptTerms'));
      }
      
      logger.log('Starting upload with file:', { 
        previewFile: previewFile ? previewFile.name : 'none'
      });
      
      // Prepare file for upload
      const files = {};
      if (previewFile) {
        files.previewFile = previewFile;
        logger.log('Adding preview file to upload:', previewFile.name);
      }
      
      // Perform the upload
      logger.log('Sending files to server...');
      const uploadResult = await uploadToServer(files, formData);
      logger.log('Upload completed successfully:', uploadResult);
      
      // Handle successful upload
      if (uploadResult && uploadResult.success) {
        // Show success message
        setSubmitSuccess(true);
        setSubmitError('');
        
        // Auto-hide success message after 8 seconds
        setTimeout(() => {
          setSubmitSuccess(false);
        }, 8000);
        
        // Scroll to top to show success message
        window.scrollTo({ top: 0, behavior: 'smooth' });
        
        // Update upload limit information if provided
        if (uploadResult.rateLimit) {
          setUploadLimit({
            count: uploadResult.rateLimit.count || 0,
            limit: uploadResult.rateLimit.limit || 3,
            remaining: uploadResult.rateLimit.remaining || 0,
            display: uploadResult.rateLimit.display || ''
          });
        }
        
        // Reset form after successful submission
        setFormData({
          username: '',
          email: '',
          title: '',
          challengeId: 'normal',
          website: '', // Reset streamer selection
          products: {
            all: false,
            mug: false,
            poster: false,
            casquette: false,
            totebag: false,
            tshirt: false,
          },
          termsAccepted: false
        });
        
        // Reset challenge selection to Normal
        setSelectedChallenge({ id: 'normal', title: 'Normal' });
        
        // Reset streamer selection
        setSelectedStreamer({ name: '', website: '' });
        
        // Reset file state
        setPreviewFile(null);
        
        // Clean up object URLs to avoid memory leaks
        if (previewFileUrl) {
          URL.revokeObjectURL(previewFileUrl);
          setPreviewFileUrl(null);
        }
      } else {
        // Handle server-side validation errors or other issues
        setSubmitError(uploadResult?.message || 'submitUploadFailed');
      }
    } catch (error) {
      logger.error('Error submitting form:', error);
      
      if (error.message && (error.message.includes('401') || error.message.includes('Authentication'))) {
        setSubmitError('submitSessionExpired');
      } else if (error.message && (error.message.includes('429') || error.message.includes('limit reached'))) {
        setSubmitError('submitLimitReached');
      } else {
        setSubmitError('submitGenericError');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show loading screen while checking authentication (EARLY RETURN like Application.js)
  // Also wait for minimum loading time to prevent shake from React StrictMode
  if (loading || !minLoadingComplete) { // Changed from isLoading to loading
    return (
      <div className="min-h-screen bg-black bg-opacity-75 text-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  // Show login form if not authenticated (EARLY RETURN like Application.jss)
  if (!user) {
    return (
      <div className="min-h-screen bg-black bg-opacity-75 text-white flex items-center justify-center">
        <div className="max-w-md w-full mx-auto px-6">
          <div className="text-center mb-8">
            <p className="text-gray-400 text-lg leading-relaxed mb-8">
              {t('loginToVoteSubmit')}
            </p>
          </div>
          
          <div className="bg-gray-900 rounded-xl p-8 border border-gray-700">
            <h2 className="text-2xl font-bold text-white mb-6 text-center">
              {t('loginRequired')}
            </h2>
            <p className="text-gray-400 text-center mb-6">
              {t('mustBeLoggedIn')}
            </p>
            
            <div className="flex justify-center">
              <GoogleAuth />
            </div>
            
            <p className="text-gray-500 text-sm text-center mt-6">
              {t('byLoggingIn')}
            </p>
          </div>
        </div>
      </div>
    );
  }

return (
  <div className="min-h-screen bg-[#121214]">
    <div className="container mx-auto py-16 px-4" id="submit-form" style={{ marginTop: '50px' }}>
      <h1 className="text-3xl md:text-4xl font-bold mb-2 text-white text-center">{t('submitADesign')}</h1>
      <p className="text-gray-400 text-center mb-6">{t('shareCreationEarnBenefits')}</p>
      
      <form onSubmit={handleSubmit} className="max-w-5xl mx-auto" noValidate>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          {/* Left column - Personal info */}
          <div className="space-y-6">
              <div className="space-y-2">
                <label htmlFor="username" className="block text-white text-sm font-medium">
                  {t('username')} <span className="text-cyan-500">*</span>
                </label>
                <input
                  type="text"
                  id="username"
                  name="username"
                  placeholder={t('usernamePlaceholder')}
                  className="w-full bg-[#121214] border border-gray-700 rounded p-3 text-white"
                  value={formData.username}
                  onChange={handleInputChange}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <label htmlFor="email" className="block text-white text-sm font-medium">
                  {t('email')} <span className="text-cyan-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  placeholder={t('emailPlaceholder')}
                  className="w-full bg-[#121214] border border-gray-700 rounded p-3 text-white"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <label htmlFor="title" className="block text-white text-sm font-medium">
                  {t('creationTitle')} <span className="text-cyan-500">*</span>
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  placeholder={t('catchyTitlePlaceholder')}
                  className="w-full bg-[#121214] border border-gray-700 rounded p-3 text-white"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                />
              </div>

            {/* Challenge Dropdown */}
            <div className="space-y-2" ref={challengeDropdownRef}>
              <label className="block text-white text-sm font-medium">
                {t('challenge')} <span className="text-gray-400 text-xs ml-1">{t('optional')}</span>
              </label>
              <div className="relative">
                {/* Selected Challenge Display */}
                  <div 
                    className="w-full bg-[#121214] border border-gray-700 rounded p-3 text-white flex justify-between items-center cursor-pointer"
                    onClick={() => setShowChallengeDropdown(!showChallengeDropdown)}
                  >
                    <div className="flex items-center">
                      {selectedChallenge.color && (
                        <div 
                          className="w-4 h-4 rounded-full mr-2" 
                          style={{ backgroundColor: selectedChallenge.color || '#6d28d9' }}
                        ></div>
                      )}
                      <span>{selectedChallenge.title}</span>
                    </div>
                    {showChallengeDropdown ? <FaChevronUp /> : <FaChevronDown />}
                  </div>
                  
                  {/* Dropdown Menu */}
                  {showChallengeDropdown && (
                    <div className="absolute z-50 mt-1 w-full bg-[#1A1A1A] border border-gray-700 rounded-md shadow-lg py-1 max-h-60 overflow-auto">
                      {/* Search Input */}
                      <div className="px-3 py-2 border-b border-gray-700">
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <FaSearch className="text-gray-400" />
                          </div>
                          <input
                            type="text"
                            className="block w-full pl-10 pr-3 py-2 bg-[#121214] border border-gray-600 rounded-md text-white text-sm focus:outline-none focus:ring-1 focus:ring-cyan-500"
                            placeholder={t('searchChallenge')}
                            value={searchQuery}
                            onChange={handleSearchChange}
                          />
                        </div>
                      </div>
                      
                      {/* Challenge List */}
                      {isLoadingChallenges ? (
                        <div className="text-center py-4">
                          <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-cyan-500 mx-auto"></div>
                        </div>
                      ) : challenges.length > 0 ? (
                        challenges
                          .filter((challenge) => challenge.id !== 0) // Filter out "No challenge" option
                          .map((challenge) => (
                          <div
                            key={challenge.id}
                            className={`px-4 py-2 hover:bg-cyan-900 cursor-pointer flex items-center ${selectedChallenge.id === challenge.id ? 'bg-cyan-800' : ''}`}
                            onClick={() => handleChallengeSelect(challenge)}
                          >
                            {challenge.color && (
                              <div 
                                className="w-3 h-3 rounded-full mr-2" 
                                style={{ backgroundColor: challenge.color || '#6d28d9' }}
                              ></div>
                            )}
                            <div>
                              <div className="text-white">{challenge.title}</div>
                              {challenge.description && (
                                <div className="text-gray-400 text-xs truncate max-w-[350px]">{challenge.description}</div>
                              )}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-4 text-gray-400">{t('noChallengeFound')}</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              
              
            </div>
            
            {/* Files upload */}
            <div className="">
              {/* Streamer Dropdown */}
              <div className="space-y-2" ref={streamerDropdownRef}>
                <label className="block text-white text-sm font-medium">
                  Streamer <span className="text-cyan-500">*</span>
                </label>
                <div className="relative">
                  {/* Selected Streamer Display */}
                  <div 
                    className="w-full bg-[#121214] border border-gray-700 rounded p-3 text-white flex justify-between items-center cursor-pointer"
                    onClick={() => setShowStreamerDropdown(!showStreamerDropdown)}
                  >
                    <div className="flex items-center">
                      <span>
                        {selectedStreamer.name ? selectedStreamer.name : t('selectStreamer')}
                      </span>
                    </div>
                    {showStreamerDropdown ? <FaChevronUp /> : <FaChevronDown />}
                  </div>
                  
                  {/* Dropdown Menu */}
                  {showStreamerDropdown && (
                    <div className="absolute z-50 mt-1 w-full bg-[#1A1A1A] border border-gray-700 rounded-md shadow-lg py-1 max-h-60 overflow-auto">
                      {/* Streamer List */}
                      {isLoadingStreamers ? (
                        <div className="text-center py-4">
                          <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-cyan-500 mx-auto"></div>
                        </div>
                      ) : availableStreamers.length > 0 ? (
                        availableStreamers.map((streamerName) => (
                          <div
                            key={streamerName}
                            className={`px-4 py-2 hover:bg-cyan-900 cursor-pointer flex items-center ${selectedStreamer.name === streamerName ? 'bg-cyan-800' : ''}`}
                            onClick={() => handleStreamerSelect(streamerName)}
                          >
                            <div className="text-white">{streamerName}</div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-4 text-gray-400">{t('noStreamerFound')}</div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Preview file upload */}
              <div className="mt-5">
                <label className="block text-white text-sm font-medium mb-2">
                  {t('previewFiles')} <span className="text-cyan-500">*</span>
                </label>
                <div className="mb-2 px-3 py-2 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                  <p className="text-xs text-yellow-400 flex items-center gap-2">
                    <span>{t('fileSizeNote')}</span>
                  </p>
                </div>
                <div className="mb-2 px-3 py-2 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                  <p className="text-xs text-yellow-400 flex items-start gap-2">
                    <span className="flex-shrink-0 mt-0.5" aria-hidden="true">
                      <svg className="w-4 h-4 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </span>
                    <span className="block">
                      <strong>{t('designUploadImportant')}</strong>
                      <br />
                      {t('designUploadNoMockups')}
                    </span>
                  </p>
                </div>
                <div 
                  className={`border-2 border-dashed ${isDragging ? 'border-cyan-400 bg-cyan-900 bg-opacity-20' : 'border-cyan-500'} rounded-lg p-4 h-44 flex flex-col items-center justify-center bg-[#121214] relative transition-all duration-200 ${isDragging ? 'scale-[1.02]' : ''}`}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                >
                  <div className="text-center">
                    {checkingBackground ? (
                      <div className="text-center py-2">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-cyan-400 mx-auto mb-3"></div>
                        <p className="text-sm text-cyan-400 font-medium">{t('checkingTransparency')}</p>
                        <p className="text-xs text-gray-500 mt-1">{t('verifyingNoBackground')}</p>
                      </div>
                    ) : previewFile ? (
                      <div className="text-center">
                        {previewFileUrl ? (
                          <>
                            <img src={previewFileUrl} alt="Preview" className="max-h-32 max-w-full mx-auto rounded shadow-lg shadow-cyan-600/20" />
                            <p className="text-sm text-gray-400 mt-2">{previewFile.name} ({Math.round(previewFile.size / 1024)} Ko)</p>
                          </>
                        ) : (
                          <div className="text-white">{previewFile.name}</div>
                        )}
                      </div>
                    ) : (
                      <>
                        <div className={`h-12 w-12 mx-auto mb-2 text-cyan-500 ${isDragging ? 'animate-bounce' : ''}`}>
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                          </svg>
                        </div>
                        <div className="text-sm text-gray-400 mb-1">{isDragging ? t('dropFileHere') : t('dragOr')}</div>
                        <div className="text-sm text-cyan-500 mb-1">{isDragging ? '' : t('browse')}</div>
                        <div className="text-xs text-gray-500">{t('pngJpgWebp')}</div>
                      </>
                    )}
                  </div>
                  <input
                    type="file"
                    id="previewFile"
                    name="previewFile"
                    accept="image/png,image/webp"
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                    onChange={handlePreviewFileChange}
                    required
                  />
                </div>
                {previewFile && (
                  <div className="mt-2 flex justify-end">
                    <button 
                      type="button" 
                      className="text-xs text-red-400 hover:text-red-300 transition-colors duration-200"
                      onClick={() => {
                        setPreviewFile(null);
                        if (previewFileUrl) {
                          URL.revokeObjectURL(previewFileUrl);
                          setPreviewFileUrl(null);
                        }
                      }}
                    >
                      {t('removeFile')}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Product selection */}
          <div className="mt-10">
            <label className="block text-white text-sm font-medium mb-4">
              {t('suitableProducts')} <span className="text-cyan-500">*</span>
            </label>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="all"
                  name="all"
                  checked={formData.products.all}
                  onChange={handleCheckboxChange}
                  className="h-4 w-4 text-cyan-600 border-gray-300 rounded focus:ring-cyan-500"
                />
                <label htmlFor="all" className="ml-2 text-sm text-white font-semibold">{t('all')}</label>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="mug"
                  name="mug"
                  checked={formData.products.mug}
                  onChange={handleCheckboxChange}
                  className="h-4 w-4 text-cyan-600 border-gray-300 rounded focus:ring-cyan-500"
                />
                <label htmlFor="mug" className="ml-2 text-sm text-white">Mug</label>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="poster"
                  name="poster"
                  checked={formData.products.poster}
                  onChange={handleCheckboxChange}
                  className="h-4 w-4 text-cyan-600 border-gray-300 rounded focus:ring-cyan-500"
                />
                <label htmlFor="poster" className="ml-2 text-sm text-white">Poster</label>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="casquette"
                  name="casquette"
                  checked={formData.products.casquette}
                  onChange={handleCheckboxChange}
                  className="h-4 w-4 text-cyan-600 border-gray-300 rounded focus:ring-cyan-500"
                />
                <label htmlFor="casquette" className="ml-2 text-sm text-white">{t('cap')}</label>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="totebag"
                  name="totebag"
                  checked={formData.products.totebag}
                  onChange={handleCheckboxChange}
                  className="h-4 w-4 text-cyan-600 border-gray-300 rounded focus:ring-cyan-500"
                />
                <label htmlFor="totebag" className="ml-2 text-sm text-white">Totebag</label>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="tshirt"
                  name="tshirt"
                  checked={formData.products.tshirt}
                  onChange={handleCheckboxChange}
                  className="h-4 w-4 text-cyan-600 border-gray-300 rounded focus:ring-cyan-500"
                />
                <label htmlFor="tshirt" className="ml-2 text-sm text-white">T-shirt</label>
              </div>
            </div>
          </div>
          
          {/* Terms and conditions */}
          <div className="mt-8">
            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  id="termsAccepted"
                  name="termsAccepted"
                  type="checkbox"
                  checked={formData.termsAccepted}
                  onChange={handleCheckboxChange}
                  className="h-4 w-4 text-cyan-600 border-gray-300 rounded focus:ring-cyan-500"
                  required
                />
              </div>
              <div className="ml-3 text-sm">
                <label htmlFor="termsAccepted" className="text-gray-400">
                  {t('termsAgreement')} <Link to="/terms" className="text-cyan-500 hover:text-cyan-400">{t('cessionContract')}</Link> {t('andUnderstand')}
                </label>
              </div>
            </div>
          </div>
          
          {/* Upload limit indicator */}
          <div className="mt-6 mb-2 text-center">
            {/* Upload counter removed as requested */}
          </div>
          
          {/* Submit button */}
          <div className="text-center">
            <button
              type="submit"
              className="bg-cyan-600 hover:bg-cyan-700 text-white font-bold py-3 px-6 rounded-md transition duration-300 flex items-center mx-auto"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {t('sending')}
                </>
              ) : (
                <>
                  {t('submitMyDesign')}
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </>
              )}
            </button>
          </div>
          
          {/* Feedback messages */}
          {submitError && (
            <div className="mt-4 p-3 bg-red-900 bg-opacity-30 text-red-400 rounded-md">
              <p className="font-semibold">{t('anErrorOccurred')}</p>
              <p>{t(submitError)}</p>
            </div>
          )}
          
          {submitSuccess && (
            <div className="mt-4 p-3 bg-green-900 bg-opacity-30 text-green-400 rounded-md">
              <p className="font-semibold">{t('thankYouSubmission')}</p>
              <p>{t('designSubmittedSuccess')}</p>
            </div>
          )}
        </form>
      </div>

      {/* How it works section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-white text-center mb-2">{t('howItWorks')}</h2>
          <p className="text-gray-400 text-center mb-12">{t('makeProductEarnCommissions')}</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Card 1 - Commission */}
            <div className="bg-[#121214] border border-gray-800 rounded-lg p-6 hover:border-cyan-600 transition-all duration-300">
              <div className="h-12 w-12 bg-cyan-900 rounded-full flex items-center justify-center mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-cyan-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">{t('tenPercentBenefits')}</h3>
              <p className="text-gray-400 mb-4"><span className="font-bold">{t('receive10Percent')}</span></p>
              <p className="text-gray-400 mb-4">{t('dashboardTracking')}</p>
              <ul className="space-y-2 text-sm text-gray-400">
                <li className="flex items-start">
                  <span className="text-cyan-500 mr-2">•</span> {t('noEarningsCap')}
                </li>
                <li className="flex items-start">
                  <span className="text-cyan-500 mr-2">•</span> {t('automaticPayments')}
                </li>
                <li className="flex items-start">
                  <span className="text-cyan-500 mr-2">•</span> {t('preciseTracking')}
                </li>
              </ul>
            </div>

            {/* Card 2 - Product variety */}
            <div className="bg-[#121214] border border-gray-800 rounded-lg p-6 hover:border-cyan-600 transition-all duration-300">
              <div className="h-12 w-12 bg-cyan-900 rounded-full flex items-center justify-center mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-cyan-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">{t('productVariety')}</h3>
              <p className="text-gray-400 mb-4">{t('creationsPrinted')}</p>
              <ul className="space-y-2 text-sm text-gray-400">
                <li className="flex items-start">
                  <span className="text-cyan-500 mr-2">•</span> {t('tshirtsSweatshirts')}
                </li>
                <li className="flex items-start">
                  <span className="text-cyan-500 mr-2">•</span> {t('capsAccessories')}
                </li>
                <li className="flex items-start">
                  <span className="text-cyan-500 mr-2">•</span> {t('mugsCups')}
                </li>
                <li className="flex items-start">
                  <span className="text-cyan-500 mr-2">•</span> {t('muchMore')}
                </li>
              </ul>
            </div>

            {/* Card 3 - Viral exposure */}
            <div className="bg-[#121214] border border-gray-800 rounded-lg p-6 hover:border-cyan-600 transition-all duration-300">
              <div className="h-12 w-12 bg-cyan-900 rounded-full flex items-center justify-center mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-cyan-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">{t('viralExposure')}</h3>
              <p className="text-gray-400 mb-4">{t('networkVisibility')}</p>
              <ul className="space-y-2 text-sm text-gray-400">
                <li className="flex items-start">
                  <span className="text-cyan-500 mr-2">•</span> {t('millionViews')}
                </li>
                <li className="flex items-start">
                  <span className="text-cyan-500 mr-2">•</span> {t('hundredsPartners')}
                </li>
                <li className="flex items-start">
                  <span className="text-cyan-500 mr-2">•</span> {t('internationalDiffusion')}
                </li>
              </ul>
            </div>

            {/* Card 4 - Drop system */}
            <div className="bg-[#121214] border border-gray-800 rounded-lg p-6 hover:border-cyan-600 transition-all duration-300">
              <div className="h-12 w-12 bg-cyan-900 rounded-full flex items-center justify-center mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-cyan-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">{t('dropSystem')}</h3>
              <p className="text-gray-400 mb-4">{t('functionByDrops')}</p>
              <ul className="space-y-2 text-sm text-gray-400">
                <li className="flex items-start">
                  <span className="text-cyan-500 mr-2">•</span> {t('announcedPeriods')}
                </li>
                <li className="flex items-start">
                  <span className="text-cyan-500 mr-2">•</span> {t('trackingTools')}
                </li>
                <li className="flex items-start">
                  <span className="text-cyan-500 mr-2">•</span> {t('massiveCommunication')}
                </li>
              </ul>
            </div>
          </div>
          


          </div>
        </div>
      </section>
  </div>
);
}

export default Submit;
